import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ColumnAccessorKey, ColumnConfiguration} from 'nav/expanded/ListingRendererConstants';
import StepDocumentLink from 'notebook/worksheets/steps/StepDocumentLink';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import ProcedurePageLinkForProcedureRun from 'procedures/ProcedurePageLinkForProcedureRun';
import getProcedureReplicateName from 'procedures/utils/getProcedureReplicateNames';
import getProcedureRunConditionName from 'procedures/utils/getProcedureRunConditionNames';
import * as ProcessManagementContextMenu from 'processes/dashboard/ProcessManagementContextMenu';
import ProcessTaskCellTitle from 'processes/dashboard/ProcessTaskCellTitle';
import {
  ProcessTaskAssigneeDisplay,
  ProcessTaskStatusDisplay,
} from 'processes/dashboard/ProcessTaskDashboardDisplay';
import {processExecutionTypeDisplayName} from 'processes/ProcessConstants';
import {NO_ACCESS_TEXT} from 'processes/ProcessConstants';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import {
  getStudiesRowContentHeight,
  maybeGetStudiesListingColumn,
  StudyableItemListingExtras,
} from 'studies/studyable/StudiesListingColumn';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcessTaskChip from 'ui-library/chips/ProcessTaskChip';
import DateTableCell from 'ui-library/scrollable/table-cells/DateTableCell';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {BasicEvent} from 'util/Types';

import {ProcessTaskListingGraphQL} from '../../../../../../graphql-fragments/processes/__generated/ProcessTaskListingGraphQL';
import {
  getProcedureColumnData,
  ProcedureColumnKey,
  ProcedureListingRenderer,
  ProcedureListingRendererType,
} from './ProcedureListingConstants';

interface ProcedureStepTaskListingRendererExtras extends StudyableItemListingExtras {}

export default abstract class ProcedureStepTaskListingRenderer<
  T extends ProcedureListingRendererType,
> extends ProcedureListingRenderer<T> {
  getNonPluginColumnConfigurations(
    items: Array<ProcessTaskListingGraphQL>,
    {itemStudyAttributes}: ProcedureStepTaskListingRendererExtras = {}
  ): Array<ColumnConfiguration<ProcessTaskListingGraphQL>> {
    const maybeStudiesColumn = maybeGetStudiesListingColumn<ProcessTaskListingGraphQL>(itemStudyAttributes);

    return [
      {
        column: {
          accessorKey: ColumnAccessorKey.NAME,
          header: 'Name',
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
          width: 200,
        },
        render: (item) => item.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.PROCESS_TASK_TYPE,
          header: 'Task type',
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_PROCESS_TASK_TYPE,
        },
        render: (item) => processExecutionTypeDisplayName(this.clientOrigin, item.executionType),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.STATUS,
          header: getColumnHeaderFromSortKey(SortKey.SORT_STATUS),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_STATUS,
        },
        render: (item) => <ProcessTaskStatusDisplay status={item.status} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.ASSIGNEE,
          header: getColumnHeaderFromSortKey(SortKey.SORT_ASSIGNEE_NAME),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_ASSIGNEE_NAME,
        },
        render: (item: ProcessTaskListingGraphQL) => <ProcessTaskAssigneeDisplay assignee={item.assignee} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.WORKSHEET,
          header: 'Worksheet',
          flexGrow: 1,
          isSortable: false,
        },
        render: (item) => {
          let document = null;
          if (
            item.procedureStepExecutionInstance != null &&
            !isInaccessible(item.procedureStepExecutionInstance) &&
            !isInaccessible(item.procedureStepExecutionInstance.document)
          ) {
            document = item.procedureStepExecutionInstance.document;
          }

          return document != null ? (
            <StepDocumentLink href={document.editUrl}>{document.name}</StepDocumentLink>
          ) : null;
        },
      },
      {
        column: {
          ...getProcedureColumnData(ProcedureColumnKey.PROCEDURE_CONDITIONS, this.clientOrigin),
          flexGrow: 1,
        },
        render: (item) => {
          if (
            item.procedureStepExecutionInstance != null &&
            !isInaccessible(item.procedureStepExecutionInstance)
          ) {
            return getProcedureRunConditionName(item.procedureStepExecutionInstance.methodExecutionInstance);
          }
          return null;
        },
      },
      {
        column: {
          ...getProcedureColumnData(ProcedureColumnKey.PROCEDURE_REPLICATE, this.clientOrigin),
          flexGrow: 1,
        },
        render: (item) => {
          if (
            item.procedureStepExecutionInstance != null &&
            !isInaccessible(item.procedureStepExecutionInstance)
          ) {
            return getProcedureReplicateName(item.procedureStepExecutionInstance.methodExecutionInstance);
          }
          return null;
        },
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.SCHEDULED_ON,
          header: getColumnHeaderFromSortKey(SortKey.SORT_SCHEDULED_ON_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_SCHEDULED_ON_KEY,
        },
        render: (item: ProcessTaskListingGraphQL) => <DateTableCell date={item.scheduledOnDate} />,
      },
      {
        column: {
          ...getProcedureColumnData(ProcedureColumnKey.PROCEDURE_TEMPLATE, this.clientOrigin),
          flexGrow: 1,
        },
        render: (item) => {
          if (
            item.procedureStepExecutionInstance != null &&
            !isInaccessible(item.procedureStepExecutionInstance)
          ) {
            const procedureRun = item.procedureStepExecutionInstance.methodExecutionInstance.procedureRun;
            return procedureRun != null ? (
              <ProcedurePageLinkForProcedureRun procedureRun={procedureRun} />
            ) : null;
          } else {
            return null;
          }
        },
      },
      ...(maybeStudiesColumn ? [maybeStudiesColumn] : []),
      {
        column: {
          ...getProcedureColumnData(ProcedureColumnKey.PROCEDURE_RUN, this.clientOrigin),
          flexGrow: 1,
          width: 200,
        },
        render: (item) => {
          if (
            item.procedureStepExecutionInstance != null &&
            !isInaccessible(item.procedureStepExecutionInstance)
          ) {
            const procedureReplicate =
              item.procedureStepExecutionInstance?.methodExecutionInstance.procedureRunConditionReplicate;
            if (procedureReplicate && procedureReplicate.processRootTask) {
              const taskId = getAccessibleOrInaccessibleId(procedureReplicate.processRootTask);
              return <ProcessTaskChip id={taskId} size={ChipSize.MINI} />;
            }
          }
          return null;
        },
      },
      {
        column: {
          ...getProcedureColumnData(ProcedureColumnKey.PROCEDURE_METHOD, this.clientOrigin),
          flexGrow: 1,
          width: 200,
        },
        render: (item) => {
          if (
            item.procedureStepExecutionInstance != null &&
            !isInaccessible(item.procedureStepExecutionInstance)
          ) {
            const methodTask = item.procedureStepExecutionInstance.methodExecutionInstance.methodTask;
            if (methodTask) {
              const taskId = getAccessibleOrInaccessibleId(methodTask);
              return <ProcessTaskChip id={taskId} size={ChipSize.MINI} />;
            }
            const templateMethodVersion =
              item.procedureStepExecutionInstance?.methodExecutionInstance?.templateMethodVersion;
            if (templateMethodVersion == null) {
              return null;
            } else if (isInaccessible(templateMethodVersion)) {
              return NO_ACCESS_TEXT;
            }
            return templateMethodVersion.name;
          }
          return null;
        },
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.ID,
          header: getColumnHeaderFromSortKey(SortKey.SORT_IDENTIFIER_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_IDENTIFIER_KEY,
        },
        render: (item) => <ProcessTaskCellTitle processTask={item} title={item.humanId} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.modifiedAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATOR,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATOR_NAME),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATOR_NAME,
        },
        render: (item) => getPartyDisplayString(item.creator),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
    ];
  }

  getNonPluginRowContentHeight(
    item: ProcessTaskListingGraphQL,
    {itemStudyAttributes}: ProcedureStepTaskListingRendererExtras
  ): number {
    return Math.max(
      ExpandedTableTagUtil.getRowHeight(item.fields),
      getStudiesRowContentHeight(item.id, itemStudyAttributes)
    );
  }

  openContextMenu(item: ProcessTaskListingGraphQL, e: BasicEvent, extras: unknown): void {
    ProcessManagementContextMenu.open(e.nativeEvent, ModelTypeConstants.PROCESS_TASK, item);
  }
}
